/**
 * sl-vw37.1.3: VerticalIntegrationTile — Generic tile for vertical integrations
 *
 * Renders a single vertical in the workspace settings integration list.
 * Shows 4 states:
 *   1. Plan-locked: Feature not in plan → "Upgrade" badge
 *   2. Not provisioned: allowed, docId === null → "Einrichten" button
 *   3. Enabled: provisioned + enabled → green status + disable toggle
 *   4. Disabled: provisioned + !enabled → enable toggle
 *
 * API calls use generic /api/workspace/:id/vertical/:type/ endpoints.
 *
 * SOLID:
 * - S: Only renders a single vertical tile with its state
 * - O: Works for any registered vertical via FrontendVerticalDefinition
 */
import { Switch } from '@affine/component';
import type { FrontendVerticalDefinition } from '@affine/core/modules/streamlain/constants/vertical-definitions';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { LockIcon } from '@blocksuite/icons/rc';
import { cssVar } from '@streamlain/theme';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { requestWorkspaceSetting } from '../shared/workspace-settings-api';
import {
  IntegrationCard,
  IntegrationCardContent,
  IntegrationCardFooter,
  IntegrationCardHeader,
} from './card';

// ============================================================================
// Types
// ============================================================================

interface VerticalConfig {
  verticalType: string;
  docId: string | null;
  enabled: boolean;
  metadata: unknown;
}

type TileState =
  | 'loading'
  | 'locked'
  | 'not-provisioned'
  | 'enabled'
  | 'disabled';

// ============================================================================
// API Helpers
// ============================================================================

async function fetchVerticalConfig(
  workspaceId: string,
  type: string
): Promise<VerticalConfig | null> {
  const res = await requestWorkspaceSetting(
    workspaceId,
    `/api/workspace/${workspaceId}/vertical/${type}/config`,
    undefined
  );
  if (!res.ok) return null;
  return res.json();
}

async function provisionVertical(
  workspaceId: string,
  type: string
): Promise<{ docId: string; enabled: boolean } | null> {
  const res = await requestWorkspaceSetting(
    workspaceId,
    `/api/workspace/${workspaceId}/vertical/${type}/provision`,
    { method: 'POST' }
  );
  if (!res.ok) return null;
  return res.json();
}

async function setVerticalEnabled(
  workspaceId: string,
  type: string,
  enabled: boolean
): Promise<boolean> {
  const res = await requestWorkspaceSetting(
    workspaceId,
    `/api/workspace/${workspaceId}/vertical/${type}/enabled`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }
  );
  return res.ok;
}

// ============================================================================
// Hook
// ============================================================================

function useVerticalTileState(def: FrontendVerticalDefinition): {
  state: TileState;
  config: VerticalConfig | null;
  onProvision: () => Promise<void>;
  onToggle: (enabled: boolean) => Promise<void>;
} {
  const workspaceId = useService(WorkspaceService).workspace.id;
  const { isFeatureAllowed, features } = useFeatureGate();
  const [config, setConfig] = useState<VerticalConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const allowed = isFeatureAllowed(def.featureKey);
  const isHidden =
    features.find(f => f.featureKey === def.featureKey)?.visibility ===
    'hidden';

  // Fetch initial config — skip for hidden or disallowed features
  useEffect(() => {
    if (!allowed || isHidden) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    fetchVerticalConfig(workspaceId, def.id)
      .then(c => {
        if (!cancelled) setConfig(c);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, def.id, allowed, isHidden]);

  const onProvision = useCallback(async () => {
    const result = await provisionVertical(workspaceId, def.id);
    if (result) {
      setConfig(prev => ({
        verticalType: def.id,
        docId: result.docId,
        enabled: result.enabled,
        metadata: prev?.metadata ?? null,
      }));
    }
  }, [workspaceId, def.id]);

  const onToggle = useCallback(
    async (enabled: boolean) => {
      const ok = await setVerticalEnabled(workspaceId, def.id, enabled);
      if (ok) {
        setConfig(prev => (prev ? { ...prev, enabled } : null));
      }
    },
    [workspaceId, def.id]
  );

  let state: TileState;
  if (isLoading) {
    state = 'loading';
  } else if (!allowed) {
    state = 'locked';
  } else if (!config) {
    state = 'not-provisioned';
  } else if (config.enabled) {
    state = 'enabled';
  } else {
    state = 'disabled';
  }

  return { state, config, onProvision, onToggle };
}

// ============================================================================
// Component
// ============================================================================

// ============================================================================
// Main Tile Component
// ============================================================================

export function VerticalIntegrationTile({
  definition,
}: {
  definition: FrontendVerticalDefinition;
}) {
  const { features, isFeatureAllowed, getRequiredPlan, notifyFeatureLocked } =
    useFeatureGate();
  const { state, onProvision, onToggle } = useVerticalTileState(definition);

  const allowed = isFeatureAllowed(definition.featureKey);
  const requiredPlan = getRequiredPlan(definition.featureKey);

  // sl-lrcr: Hidden features are always invisible
  const feature = features.find(f => f.featureKey === definition.featureKey);
  const isHidden = feature?.visibility === 'hidden';

  const handleClick = useCallback(() => {
    if (!allowed) {
      notifyFeatureLocked(definition.featureKey);
      return;
    }
    if (state === 'not-provisioned') {
      onProvision().catch(console.error);
    }
  }, [allowed, state, onProvision, notifyFeatureLocked, definition.featureKey]);

  if (isHidden) {
    return null;
  }

  return (
    <li>
      <IntegrationCard
        onClick={handleClick}
        style={
          state === 'locked' ? { opacity: 0.6, cursor: 'default' } : undefined
        }
      >
        <IntegrationCardHeader
          icon={definition.tile.icon}
          title={definition.tile.name}
          status={
            state === 'enabled' ? (
              <span
                style={{
                  color: cssVarV2('status/success'),
                  fontSize: cssVar('fontXs'),
                }}
              >
                Active
              </span>
            ) : undefined
          }
        />
        <IntegrationCardContent desc={definition.tile.description} />

        {/* Plan-locked footer */}
        {state === 'locked' && requiredPlan && (
          <IntegrationCardFooter>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: cssVar('fontXs'),
                color: cssVarV2('text/secondary'),
              }}
            >
              <LockIcon style={{ fontSize: 14 }} />
              Upgrade to {requiredPlan}
            </div>
          </IntegrationCardFooter>
        )}

        {/* Not provisioned: "Einrichten" action */}
        {state === 'not-provisioned' && (
          <IntegrationCardFooter>
            <div
              style={{
                fontSize: cssVar('fontXs'),
                color: cssVarV2('button/primary'),
                cursor: 'pointer',
              }}
            >
              Einrichten
            </div>
          </IntegrationCardFooter>
        )}

        {/* Enabled/Disabled: Toggle switch */}
        {(state === 'enabled' || state === 'disabled') && (
          <IntegrationCardFooter>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Switch
                checked={state === 'enabled'}
                onChange={(checked: boolean) => {
                  onToggle(checked).catch(console.error);
                }}
              />
            </div>
          </IntegrationCardFooter>
        )}
      </IntegrationCard>
    </li>
  );
}
